/* eslint-disable max-lines */
import {
  CbhIcon,
  Pill,
  ScrollingNumberCounter,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { CLIPBOARD_SCORE } from "@src/appV2/ClipboardScore/constants";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getOrdinalPostfix } from "@src/appV2/lib/utils/formatNumbers";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useEffect, useState } from "react";

import { Button } from "../../../components/Button";

const SCORE_ANIMATION_DURATION_MS = 1500;

interface ClipboardScoreBottomSheetProps {
  modalState: UseModalState;
  currentScore: number;
  scoreChange: number;
  scoreChangeReasons: string[];
  ranking?: {
    rank?: number;
    totalWorkers?: number;
    workerType?: string;
  };
  shiftId: string;
  onContinue: () => void;
  isFirstShiftInDouble?: boolean;
  isSecondShiftInDouble?: boolean;
  isAlreadyRestricted?: boolean;
  newlyRestricted?: boolean;
  latenessMinutes?: number;
  wasLastMinuteBooking?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  formattedTargetArrivalTime?: string;
}

export function ClipboardScoreBottomSheet(props: ClipboardScoreBottomSheetProps) {
  const {
    modalState,
    currentScore,
    scoreChange,
    scoreChangeReasons,
    ranking,
    shiftId,
    onContinue,
    isFirstShiftInDouble = false,
    isSecondShiftInDouble = false,
    isAlreadyRestricted = false,
    newlyRestricted = false,
    latenessMinutes,
    wasLastMinuteBooking = false,
    isArrivalTimeFeatureEnabled = false,
    formattedTargetArrivalTime,
  } = props;

  const [showContinueButton, setShowContinueButton] = useState(false);
  const [startAnimation, setStartAnimation] = useState(false);

  const startScore = currentScore - scoreChange;
  const endScore = currentScore;

  useEffect(() => {
    if (modalState.modalIsOpen && !isAlreadyRestricted) {
      setShowContinueButton(false);
      setStartAnimation(false);

      // Start animation after 1 second delay
      const animationTimer = setTimeout(() => {
        setStartAnimation(true);
      }, 1000);

      // Show continue button after animation completes (1s delay + 1.5s animation = 2.5s total)
      const continueButtonTimer = setTimeout(() => {
        setShowContinueButton(true);
      }, 1000 + SCORE_ANIMATION_DURATION_MS);

      return () => {
        clearTimeout(animationTimer);
        clearTimeout(continueButtonTimer);
      };
    }

    return undefined;
  }, [modalState.modalIsOpen, isAlreadyRestricted]);

  useLogEffect(
    APP_V2_USER_EVENTS.CLIPBOARD_SCORE_BOTTOM_SHEET_VIEWED,
    {
      shiftId,
      currentScore,
      scoreChange,
      isSecondShiftInDouble,
      latenessMinutes,
      wasLastMinuteBooking,
    },
    {
      enabled: modalState.modalIsOpen,
    }
  );

  // eslint-disable-next-line complexity
  const getScoreMessages = (): { title: string; subtitle?: string } => {
    // Double shift - no tardiness points scenario
    if (isSecondShiftInDouble && scoreChange >= 0) {
      return {
        title: `You earned ${scoreChange} points for completing your shift.`,
        subtitle:
          "Since this was the second shift in a double, you didn't earn or lose points related to your arrival time.",
      };
    }

    // Last minute booking scenarios
    if (wasLastMinuteBooking && scoreChange > 0) {
      // If they were late but still earned points (due to last-minute booking protection)
      if (isDefined(latenessMinutes) && latenessMinutes > 0) {
        return {
          title: `You earned ${scoreChange} points for completing your shift.`,
          subtitle: isArrivalTimeFeatureEnabled
            ? undefined
            : `Since this shift was booked within 4 hours of the start time, you didn't lose points for arriving late.`,
        };
      }

      // If they arrived on time for a last-minute booking, they earned the full points
      return {
        title: `You earned ${scoreChange} points for arriving on time and completing your shift!`,
        subtitle: "Great job arriving on time for this short-notice shift!",
      };
    }

    // Last minute booking with no score change
    if (wasLastMinuteBooking && scoreChange === 0) {
      return {
        title: `Your score remains at ${currentScore}.`,
        // we're not gonna show this message if the arrival time feature is enabled
        subtitle: isArrivalTimeFeatureEnabled
          ? undefined
          : `Since this shift was booked within 4 hours of the start time, you didn't lose points for arriving late.`,
      };
    }

    const isArrivalTimeEnabled =
      isArrivalTimeFeatureEnabled && isDefined(formattedTargetArrivalTime);

    // 5+ minutes late - missed timeliness bonus or (probably) lost points
    if (isDefined(latenessMinutes) && latenessMinutes >= 5) {
      let subtitle =
        "You missed out on the on-time bonus points! Plan to arrive early for your next shift.";

      if (isArrivalTimeEnabled) {
        subtitle = `You had committed to arriving by ${formattedTargetArrivalTime} (the time shown when you booked the shift), but you arrived ${latenessMinutes} minutes late.`;
      } else if (isFirstShiftInDouble) {
        subtitle = `Heads up! This is part of a double shift, so your next shift is not eligible for the 5 point on-time arrival bonus.`;
      } else if (latenessMinutes > 15 && scoreChange <= 0) {
        subtitle = "Being on time prevents gaps in coverage and helps with a smooth shift change.";
      }

      return {
        title:
          scoreChange >= 0
            ? `You earned ${scoreChange} points for completing your shift.`
            : `You lost ${Math.abs(scoreChange)} points for being ${latenessMinutes} minutes late.`,
        subtitle,
      };
    }

    // Standard positive score change
    if (scoreChange > 0) {
      return {
        title: `You earned ${scoreChange} points for arriving on time and completing your shift!`,
      };
    }

    // No score change
    if (scoreChange === 0) {
      return {
        title: `Your score remains at ${currentScore}.`,
        subtitle: `Arriving on time and completing your shift can help boost your ${CLIPBOARD_SCORE}.`,
      };
    }

    // Standard negative score change
    const formattedReasons = scoreChangeReasons.join(" and ");
    return {
      title: `You lost ${Math.abs(scoreChange)} points due to ${formattedReasons}.`,
      subtitle: `Arriving on time and completing your shift can help boost your ${CLIPBOARD_SCORE}.`,
    };
  };

  const getBackgroundGradient = (): string => {
    // Green gradient for earned points or special scenarios (double shifts, last minute bookings)
    if (scoreChange > 0 || isSecondShiftInDouble || wasLastMinuteBooking) {
      return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(218, 241, 232, 1) 100%)";
    }

    // Green gradient for 5-15 min late with positive score (missed timeliness bonus but still earned points)
    if (
      isDefined(latenessMinutes) &&
      latenessMinutes >= 5 &&
      latenessMinutes < 15 &&
      scoreChange >= 0
    ) {
      return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(218, 241, 232, 1) 100%)";
    }

    // Red gradient for newly restricted OR 15+ minutes late with negative score change and score < 100
    if (
      newlyRestricted ||
      (isDefined(latenessMinutes) && latenessMinutes >= 15 && scoreChange < 0 && currentScore < 100)
    ) {
      return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(247, 208, 208, 1) 100%)";
    }

    // Orange/yellow gradient for 15+ minutes late with non-positive score change and score >= 100
    if (
      isDefined(latenessMinutes) &&
      latenessMinutes >= 15 &&
      scoreChange <= 0 &&
      currentScore >= 100
    ) {
      return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(255, 236, 219, 1) 100%)";
    }

    // Default amber gradient for other scenarios (lost points, no change, etc.)
    return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(255, 236, 219, 1) 100%)";
  };

  const handleContinueClick = (): void => {
    logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_CONTINUE_CLICKED, {
      shiftId,
      currentScore,
      scoreChange,
    });
    onContinue();
  };

  const { title, subtitle } = getScoreMessages();

  return (
    <FullScreenDialog
      modalState={modalState}
      variant="tertiary"
      PaperProps={{
        sx: {
          background: getBackgroundGradient(),
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={9}
          sx={{
            paddingTop: 10,
            paddingX: 5,
            textAlign: "center",
          }}
        >
          <Stack spacing={6} alignItems="center">
            <Text
              variant="body2"
              textAlign="center"
              sx={{
                paddingX: 6,
                color: (theme) => theme.palette.grey[600],
              }}
            >
              Your Clipboard Score
            </Text>
            <Box
              sx={{
                transform: "scale(2.2)",
                transformOrigin: "center",
                paddingY: 8,
              }}
            >
              <ScrollingNumberCounter
                startValue={startScore}
                endValue={startAnimation ? endScore : startScore}
                duration={SCORE_ANIMATION_DURATION_MS}
                variant="h1"
                color="rgba(0, 67, 69, 1)"
              />
            </Box>

            <Stack spacing={6} alignItems="center">
              <Text
                semibold
                variant="h5"
                textAlign="center"
                sx={{
                  paddingX: 6,
                  color: "rgba(0, 67, 69, 1)",
                }}
              >
                {title}
              </Text>
              {isDefined(subtitle) && (
                <Text
                  variant="body1"
                  textAlign="center"
                  sx={{
                    paddingX: 6,
                    color: (theme) => theme.palette.text?.secondary ?? "",
                  }}
                >
                  {subtitle}
                </Text>
              )}
            </Stack>
          </Stack>

          <Stack spacing={5} alignItems="center">
            {newlyRestricted ? (
              <Pill
                label={
                  <Stack direction="row" gap={3} alignItems="center">
                    <CbhIcon type="clipboard-score-unhealthy-colored" size="medium" />
                    <Text
                      semibold
                      variant="body2"
                      sx={{
                        color: (theme) => theme.palette.error?.main ?? "",
                      }}
                    >
                      Restricted
                    </Text>
                  </Stack>
                }
                sx={{
                  backgroundColor: (theme) => theme.palette.background?.tertiary ?? "",
                }}
              />
            ) : (
              isDefined(ranking) &&
              isDefined(ranking.rank) &&
              isDefined(ranking.totalWorkers) &&
              isDefined(ranking.workerType) && (
                <Pill
                  label={
                    <Stack direction="row" gap={3} alignItems="center">
                      <CbhIcon
                        type={
                          scoreChange > 0 || isSecondShiftInDouble || wasLastMinuteBooking
                            ? "clipboard-score-healthy-colored"
                            : "clipboard-score-unhealthy-colored"
                        }
                        size="medium"
                      />
                      <Text semibold variant="body2">
                        {ranking.rank}
                        {getOrdinalPostfix(ranking.rank)} of {ranking.totalWorkers} local{" "}
                        {ranking.workerType}s
                      </Text>
                    </Stack>
                  }
                  sx={{
                    backgroundColor: (theme) => theme.palette.background?.tertiary ?? "",
                    color: (theme) => theme.palette.text?.primary ?? "",
                  }}
                />
              )
            )}
          </Stack>
        </Stack>
      </Box>
      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          data-testid="attendance-score-continue-button"
          sx={{ visibility: showContinueButton ? "visible" : "hidden" }}
          onClick={handleContinueClick}
        >
          Continue
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
/* eslint-enable max-lines */
